// Trainee quiz page screens. Pure: data in, HTML out.
// Words: TRAINEE_QUIZ_WORDS.md. Mockup: MOCKUP_TRAINEE_QUIZ_APPROVED.html (2026-10-05).
// Buttons say the words, every word capitalized, no arrows.

import type { NodeId } from "./training";
import { qewTrainingCard, qewTrainingCardHtml } from "./qew-training";
import type { OptionShown, QuestionShown, Stop } from "./training-server";

// ---- What the server sends back (functions/src/quiz.ts) ----

export interface QuizInfo {
  quizId: string;
  quizName: string;
  nodeId: NodeId;
  nodeName: string;
  slideFrom: number;
  slideTo: number;
  passMarkPct: number;
  questionCount: number;
  requiredCorrect: number;
}

export interface NodeDates {
  nodeId: NodeId;
  completedOn: string;
  expiresOn: string | null;
}

export interface MissedForCorrection {
  questionId: string;
  questionNumber: number;
  questionText: string;
  options: OptionShown[];
  firstPick: string;
  picks: string[];
  corrected: boolean;
}

export interface MissedWithAnswer {
  questionId: string;
  questionNumber: number;
  questionText: string;
  picked: string;
  pickedText: string;
  correctAnswer: string;
  correctText: string;
}

export interface LoadOk {
  ok: true;
  tagId: string;
  name: string;
  siteNumber: string;
  siteName: string;
  sessionCode: string;
  nodesDone: NodeId[];
  records: NodeDates[];
  today: string;
  quiz: QuizInfo;
  questions?: QuestionShown[];
  pendingCorrection?: { attemptId: string; scorePct: number; questionsCorrect: number; questionsTotal: number; missed: MissedForCorrection[] };
}

export type LoadResult = LoadOk | { ok: false; stop: Stop };

export interface SubmitOk {
  ok: true;
  attemptId: string;
  isPassed: boolean;
  scorePct: number;
  questionsCorrect: number;
  questionsTotal: number;
  passMarkPct: number;
  nodeId: NodeId;
  nodeName: string;
  completedOn: string | null;
  expiresOn: string | null;
  missed: (MissedWithAnswer | MissedForCorrection)[];
}

export type SubmitResult = SubmitOk | { ok: false; stop: Stop };

export type CheckResult =
  | { ok: false; code: string; message: string }
  | {
      ok: true;
      right: boolean;
      remaining: number;
      finished: boolean;
      firstScorePct: number;
      correctedScorePct: number | null;
      nodeId: NodeId;
      nodeName: string;
      completedOn: string | null;
      expiresOn: string | null;
    };

// ---- Helpers ----

export function esc(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** 2027-09-17 reads 09/17/2027. */
export function us(iso: string | null): string {
  if (!iso) return "Does not expire";
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${m}/${d}/${y}` : iso;
}

function nodeNumber(nodeId: NodeId): string {
  return nodeId.slice(4);
}

function shell(eyebrow: string, title: string, body: string, back = ""): string {
  return `<div class="qz"><header class="qz-bar"><div><div class="qz-e">${esc(eyebrow)}</div><div class="qz-t">${esc(title)}</div></div>${back}</header>
    <div class="qz-body">${body}</div></div>`;
}

function backBtn(act: string, label = "Back"): string {
  return `<button class="qz-back" type="button" data-act="${act}">${esc(label)}</button>`;
}

function ring(pct: number, passed: boolean): string {
  const c = 2 * Math.PI * 25;
  const off = c - (Math.max(0, Math.min(100, pct)) / 100) * c;
  const color = passed ? "var(--green)" : "var(--red)";
  return `<svg class="qz-ring" width="62" height="62" viewBox="0 0 62 62" aria-hidden="true">
    <circle cx="31" cy="31" r="25" stroke="var(--grid)" stroke-width="6" fill="none"/>
    <circle cx="31" cy="31" r="25" stroke="${color}" stroke-width="6" fill="none" stroke-dasharray="${c.toFixed(1)}" stroke-dashoffset="${off.toFixed(1)}" transform="rotate(-90 31 31)" stroke-linecap="round"/>
    <text x="31" y="35" text-anchor="middle" font-size="13" font-weight="800" fill="var(--text)">${pct}%</text></svg>`;
}

function quizEyebrow(q: Pick<QuizInfo, "quizName" | "nodeId">): string {
  return `${q.quizName.toUpperCase()} · NODE ${nodeNumber(q.nodeId)}`;
}

// ---- Screens ----

/** 1. Quiz landing. One field: the bag tag ID. */
export function landingScreen(quiz: { quizName: string; nodeName: string; nodeNumber: string } | null, tag = "", error = ""): string {
  const title = quiz ? quiz.quizName : "Knowledge Check";
  const head = quiz ? `${esc(quiz.nodeName)} <span class="st">NODE ${esc(quiz.nodeNumber)}</span>` : "Knowledge Check";
  return shell(
    "KNOWLEDGE CHECK",
    title,
    `<form class="qz-card" data-form="tag"><h3>${head}</h3>
      <label class="qz-fld"><span class="qz-lbl">Bag Tag ID</span>
        <input class="qz-in" id="qz-tag" name="tag" value="${esc(tag)}" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="01-QEW01" maxlength="10" inputmode="text" required></label>
      <div class="qz-hint">The ID on your bag tag, for example 01-QEW01.</div>
    </form>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <div class="qz-btm"><button class="qz-go" type="submit" form="qz-tagform" data-act="continue">Continue</button></div>`,
  ).replace('data-form="tag"', 'data-form="tag" id="qz-tagform"');
}

/** 2. Is this you. Name from the claimed tag. */
export function whoScreen(d: LoadOk): string {
  const next = d.nodesDone.length ? `Node ${d.nodesDone.length} complete · Node ${nodeNumber(d.quiz.nodeId)} next` : `Node ${nodeNumber(d.quiz.nodeId)} next`;
  return shell(
    quizEyebrow(d.quiz),
    "Is This You?",
    `<div class="qz-card"><div class="qz-who"><div class="qz-lbl">Name on file</div><div class="qz-nm">${esc(d.name)}</div><span class="qz-tag">${esc(d.tagId)}</span></div>
      <div class="qz-cells">
        <div class="qz-cell"><div class="qz-lbl">Site</div><div class="qz-val">${esc(d.siteName)}</div></div>
        <div class="qz-cell"><div class="qz-lbl">Session</div><div class="qz-val mono">${esc(d.sessionCode)}</div></div>
        <div class="qz-cell full"><div class="qz-lbl">Training</div><div class="qz-val">${esc(next)}</div></div>
      </div></div>
    <div class="note green">Session found at ${esc(d.siteName)} · code ${esc(d.sessionCode)}</div>
    <div class="qz-btm"><button class="qz-go" type="button" data-act="yes">Yes, That's Me</button><button class="qz-go2" type="button" data-act="notme">Not Me</button></div>`,
    backBtn("notme"),
  );
}

/** 3. Before you start. The rules in four lines. */
export function rulesScreen(q: QuizInfo): string {
  return shell(
    quizEyebrow(q),
    "Before You Start",
    `<div class="qz-card"><h3>${esc(q.nodeName)}</h3>
      <div class="qz-rl"><span>Questions</span><span>${q.questionCount}</span></div>
      <div class="qz-rl"><span>Pass mark</span><span>${q.passMarkPct}% · ${q.requiredCorrect} of ${q.questionCount}</span></div>
      <div class="qz-rl"><span>Covers</span><span>Slides ${q.slideFrom} to ${q.slideTo}</span></div>
      <div class="qz-rl"><span>Going back</span><span>Allowed until you submit</span></div></div>
    <div class="note">Use Back to check or change an answer. Once you submit, the score stands.</div>
    <div class="qz-btm"><button class="qz-go" type="button" data-act="start">Start ${esc(q.quizName)}</button></div>`,
    // One step back to Is This You (NAV_AUDIT.md item 4). Not Me there is the way to start over.
    backBtn("who"),
  );
}

/** 4. One question per screen. Next stays off until an answer is picked. */
export function questionScreen(q: QuizInfo, item: QuestionShown, index: number, total: number, picked: string | undefined): string {
  const last = index === total - 1;
  const opts = item.options
    .map(
      (o) =>
        `<button type="button" class="qz-opt${picked === o.key ? " sel" : ""}" data-act="pick" data-key="${o.key}" aria-pressed="${picked === o.key}"><span class="k">${o.key}</span><span>${esc(o.text)}</span></button>`,
    )
    .join("");
  return shell(
    quizEyebrow(q),
    `Question ${index + 1} of ${total}`,
    `<div class="qz-prog"><div class="qz-pr"><span>Progress</span><span>${index + 1} of ${total}</span></div><div class="qz-bar2"><i style="width:${Math.round((index / total) * 100)}%"></i></div></div>
    <div class="qz-card"><div class="qz-qtx">${esc(item.questionText)}</div><div class="qz-opts">${opts}</div></div>
    <div class="qz-btm"><button class="qz-go" type="button" data-act="${last ? "submit" : "next"}"${picked ? "" : " disabled"}>${last ? "Submit" : "Next"}</button></div>`,
    // Back to the last question with its answer kept; from question 1, back to the rules.
    // Nothing is scored until Submit, so going back is fair (Robert, 2026-10-22).
    backBtn("prev"),
  );
}

export function busyScreen(what: string): string {
  return `<div class="qz"><div class="qz-body"><div class="loading">${esc(what)}…</div></div></div>`;
}

/** 5a. Passed, 80 percent or more. Missed questions show the right answer. */
export function passedScreen(r: SubmitOk, quizName: string): string {
  const missed = r.missed as MissedWithAnswer[];
  const fold = missed.length
    ? `<details class="qz-fold"><summary>Missed Questions <span class="cnt">${missed.length}</span></summary>
        ${missed
          .map(
            (m) => `<div class="qz-mq"><div class="qn"><span>Question ${m.questionNumber}</span></div><div class="qt">${esc(m.questionText)}</div>
          <div class="qz-opt cmp wrong"><span class="k">${esc(m.picked)}</span><span>Your answer: ${esc(m.pickedText)}</span></div>
          <div class="qz-opt cmp right"><span class="k">${esc(m.correctAnswer)}</span><span>Correct: ${esc(m.correctText)}</span></div></div>`,
          )
          .join("")}</details>`
    : "";
  return shell(
    `${quizName.toUpperCase()} · NODE ${nodeNumber(r.nodeId)}`,
    "Quiz Passed",
    `<div class="qz-card"><h3 class="green">Node ${nodeNumber(r.nodeId)} complete <span class="st">PASSED</span></h3>
      <div class="qz-score">${ring(r.scorePct, true)}<div><b>${r.questionsCorrect} of ${r.questionsTotal} correct</b><small>Pass mark ${r.passMarkPct}%</small></div></div>
      ${r.completedOn ? `<div class="qz-cells"><div class="qz-cell"><div class="qz-lbl">Completed</div><div class="qz-val">${us(r.completedOn)}</div></div><div class="qz-cell"><div class="qz-lbl">Expires</div><div class="qz-val">${us(r.expiresOn)}</div></div></div>` : ""}
    </div>
    ${fold}
    <div class="qz-btm"><button class="qz-go" type="button" data-act="mytraining">My Training</button></div>`,
  );
}

/** 5b. Under 80 percent: the list of missed questions to correct. No answers shown. */
export function correctionListScreen(quizName: string, nodeId: NodeId, first: { scorePct: number; questionsCorrect: number; questionsTotal: number }, missed: MissedForCorrection[]): string {
  const done = missed.filter((m) => m.corrected).length;
  const dots = missed.map((m) => `<i class="d${m.corrected ? " ok" : ""}"></i>`).join("");
  const rows = missed
    .map((m) =>
      m.corrected
        ? `<div class="qz-mq done"><div class="qn"><span>Question ${m.questionNumber}</span><span class="ok">Corrected</span></div><div class="qt">${esc(m.questionText)}</div></div>`
        : `<button type="button" class="qz-mq open" data-act="fix" data-q="${esc(m.questionId)}"><div class="qn"><span>Question ${m.questionNumber}</span><span>Pick Again</span></div><div class="qt">${esc(m.questionText)}</div></button>`,
    )
    .join("");
  return shell(
    `${quizName.toUpperCase()} · NODE ${nodeNumber(nodeId)}`,
    "Correct With Your Instructor",
    `<div class="qz-card"><h3 class="amber">First try <span class="st">UNDER PASS MARK</span></h3>
      <div class="qz-score">${ring(first.scorePct, false)}<div><b>${first.questionsCorrect} of ${first.questionsTotal} correct</b><small>Work each missed question with your instructor and pick again.</small></div></div></div>
    <div class="qz-tally"><span>Corrected</span><span class="dots">${dots}</span><span>${done} of ${missed.length}</span></div>
    ${rows}
    <div class="note">Each correction saves as you go. Scan this quiz code again to pick up here.</div>
    <div class="qz-btm"><button class="qz-go2" type="button" data-act="done">Stop For Now</button></div>`,
  );
}

/** 5c. One missed question, picked again. Earlier wrong picks struck through. */
export function correctionQuestionScreen(quizName: string, m: MissedForCorrection, position: number, total: number, selected: string | undefined, wrong: boolean, error = ""): string {
  const tried = new Set([m.firstPick, ...m.picks]);
  const opts = m.options
    .map((o) => {
      const was = tried.has(o.key);
      return `<button type="button" class="qz-opt${was ? " was" : ""}${selected === o.key ? " sel" : ""}" data-act="repick" data-key="${o.key}"${was ? " disabled" : ""}><span class="k">${o.key}</span><span>${esc(o.text)}</span></button>`;
    })
    .join("");
  return shell(
    `${quizName.toUpperCase()} · CORRECTING ${position} OF ${total}`,
    `Question ${m.questionNumber}`,
    `<div class="qz-card"><div class="qz-qtx">${esc(m.questionText)}</div><div class="qz-opts">${opts}</div></div>
    ${wrong ? '<div class="note red">Not yet. Talk it through with your instructor.</div>' : ""}
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <div class="qz-btm"><button class="qz-go" type="button" data-act="check"${selected ? "" : " disabled"}>Check Answer</button></div>`,
    backBtn("list", "List"),
  );
}

/** 6. Corrected. Both scores stay on the record. */
export function correctedScreen(quizName: string, r: Extract<CheckResult, { ok: true }>, sessionCode: string): string {
  return shell(
    `${quizName.toUpperCase()} · NODE ${nodeNumber(r.nodeId)}`,
    "Corrected",
    `<div class="qz-card"><h3 class="green">Node ${nodeNumber(r.nodeId)} complete <span class="st">CORRECTED IN CLASS</span></h3>
      <div class="qz-two"><div class="s r"><b>${r.firstScorePct}%</b><small>First try</small></div><span class="ar">to</span><div class="s g"><b>${r.correctedScorePct ?? 100}%</b><small>Corrected</small></div></div>
      ${r.completedOn ? `<div class="qz-cells"><div class="qz-cell"><div class="qz-lbl">Completed</div><div class="qz-val">${us(r.completedOn)}</div></div><div class="qz-cell"><div class="qz-lbl">Expires</div><div class="qz-val">${us(r.expiresOn)}</div></div><div class="qz-cell full"><div class="qz-lbl">Session</div><div class="qz-val mono">${esc(sessionCode)}</div></div></div>` : ""}
    </div>
    <div class="note green">Both scores are on your record. Your instructor confirms the review at End Session.</div>
    <div class="qz-btm"><button class="qz-go" type="button" data-act="mytraining">My Training</button></div>`,
  );
}

/** Where My Training lands: the QEW card from the trainee's own node dates. */
export function myTrainingScreen(name: string, tagId: string, records: NodeDates[], today: string): string {
  return shell(
    "MY TRAINING",
    name,
    `${qewTrainingCardHtml(qewTrainingCard(records, today))}
    <div class="qz-hint center">${esc(tagId)}</div>
    <div class="qz-btm"><button class="qz-go2" type="button" data-act="done">Done</button></div>`,
  );
}

/** Short codes on the stop screens, as the mockup shows them. */
export const STOP_CODES: Record<Stop["code"], string> = {
  RETIRED_CODE: "RETIRED",
  BAD_CODE: "CODE",
  QUIZ_OFF: "OFF",
  TAG_NOT_CLAIMED: "TAG",
  TAG_OUT_OF_SERVICE: "TAG-OUT",
  NO_SESSION: "SESSION",
  NODE_DONE: "DONE",
  EARLIER_NODE_OPEN: "ORDER",
};

/** Stop screens: the server's reason, a short code, and a way back. */
export function stopScreen(stop: Stop, quizName: string): string {
  const icon = stop.code === "NODE_DONE" ? '<span class="ic green">✓</span>' : stop.code === "EARLIER_NODE_OPEN" ? '<span class="ic grey">!</span>' : '<span class="ic">!</span>';
  return shell(
    "KNOWLEDGE CHECK",
    quizName,
    `<div class="qz-stop">${icon}<h4>${esc(stop.title)}</h4><p>${esc(stop.message)}</p><span class="code">QZ-${esc(STOP_CODES[stop.code])}</span></div>
    <div class="qz-btm"><button class="qz-go2" type="button" data-act="done">Back</button></div>`,
  );
}

export function errorScreenQz(message: string, retryAct: string): string {
  return shell(
    "KNOWLEDGE CHECK",
    "Something Went Wrong",
    `<div class="qz-stop"><span class="ic">!</span><h4>That didn't go through</h4><p>${esc(message)}</p></div>
    <div class="qz-btm"><button class="qz-go" type="button" data-act="${retryAct}">Try Again</button><button class="qz-go2" type="button" data-act="restart">Start Over</button></div>`,
  );
}
